import { useRef, useState, useMemo, useEffect, useCallback, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

type TabValue = 'all' | 'trash'

export default function ServiceTypesPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: { name: 'Name', withSort: true, maxWidth: '200px' },
        code: { name: 'Code', withSort: true, maxWidth: '120px' },
        description: { name: 'Description', withSort: false, maxWidth: '280px' },
        active: {
          name: 'Status',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'green', false: 'gray' },
          badgeLabels: { true: 'Active', false: 'Inactive' },
        },
        approval: {
          name: 'Approval',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'blue', false: 'gray' },
          badgeLabels: { true: 'Yes', false: 'No' },
        },
        updated_at: { name: 'Updated At', withSort: true, maxWidth: '160px' },
      },
      dataSource:
        activeTab === 'trash'
          ? '/service-catalog/service-types/archived'
          : '/service-catalog/service-types',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No service types found',
      otherActions: [],
    }),
    [activeTab]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const extractTotalFromResponse = (res: unknown): number => {
    const response = res as {
      data?: { meta?: { total?: number }; data?: unknown[]; total?: number }
      meta?: { total?: number }
      total?: number
    }
    return (
      response?.data?.meta?.total ??
      response?.meta?.total ??
      response?.data?.total ??
      response?.total ??
      0
    )
  }

  const loadParentCounts = useCallback(async () => {
    try {
      const [allRes, trashRes] = await Promise.all([
        apiClient.get('/service-catalog/service-types', { params: { per_page: 1, trash: 0, parent_id: 'null' } }),
        apiClient.get('/service-catalog/service-types/archived', { params: { per_page: 1, trash: 1, parent_id: 'null' } }),
      ])
      setTotalRows(extractTotalFromResponse(allRes.data))
      setTotalTrash(extractTotalFromResponse(trashRes.data))
    } catch (error: unknown) {
      showError(error as Error)
    }
  }, [showError])

  useEffect(() => {
    loadParentCounts()
  }, [loadParentCounts])

  const tableParams = useMemo(
    () => ({ search: searchQuery, trash: activeTab === 'trash' ? 1 : 0, parent_id: 'null' }),
    [searchQuery, activeTab]
  )

  const setSubSub = useMemo(
    () => (_all: number, _trashed: number) => {
      // Keep tab counts fixed to parent-level service types.
      loadParentCounts()
    },
    [loadParentCounts]
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these service types. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url =
        bulkAction === 'restore'
          ? '/service-catalog/service-types/restore/' + selectedIds[0]
          : activeTab === 'trash'
            ? '/service-catalog/service-types/force-delete/' + selectedIds[0]
            : null
      if (bulkAction === 'restore' && url) {
        await apiClient.put(url)
        showSuccess('Success', 'Service type restored successfully')
      } else if (bulkAction === 'delete' && activeTab === 'trash' && url) {
        await apiClient.delete(url)
        showSuccess('Success', 'Service type permanently deleted')
      } else if (bulkAction === 'delete' && activeTab === 'all') {
        await apiClient.delete(`/service-catalog/service-types/${selectedIds[0]}`)
        showSuccess('Success', 'Service type moved to trash')
      }
      for (let i = 1; i < selectedIds.length; i++) {
        const id = selectedIds[i]
        if (bulkAction === 'restore') {
          await apiClient.put(`/service-catalog/service-types/restore/${id}`)
        } else if (bulkAction === 'delete' && activeTab === 'trash') {
          await apiClient.delete(`/service-catalog/service-types/force-delete/${id}`)
        } else if (bulkAction === 'delete' && activeTab === 'all') {
          await apiClient.delete(`/service-catalog/service-types/${id}`)
        }
      }
      if (selectedIds.length > 1) {
        showSuccess('Success', `${selectedIds.length} items updated`)
      }
      tableRef.current?.reload()
      loadParentCounts()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const customRenderers = useMemo(
    () => ({
      updated_at: (value: string | null | undefined) => {
        if (!value) return '–'
        try {
          return new Date(value).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        } catch {
          return String(value)
        }
      },
    }),
    []
  )

  return (
    <AdminLayout>
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Service Types
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Manage service types and categories for the catalog
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    <Tab>
                      All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <Box pb={2}>
                  <ActionButtons
                    pathname="/service-catalog/service-types"
                    createRoute="/service-catalog/service-types/create"
                    createLabel="Create Service Type"
                  />
                </Box>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              {access.can_delete && (
                <HStack spacing={2} minW="250px">
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                  >
                    {activeTab === 'trash' && <option value="restore">Restore</option>}
                    {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                    {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                  </Select>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleBulkActionClick}
                    isLoading={isBulkProcessing}
                  >
                    Apply
                  </Button>
                </HStack>
              )}
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by name, code..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody p={0} pt={1} pb={4} mx={5} flex={1} overflow="hidden" display="flex" flexDirection="column" minH={0}>
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                setSubSub={setSubSub}
                access={permissions}
                customRenderers={customRenderers}
              />
            </Box>
          </CardBody>
        </Card>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirm</ModalHeader>
            <ModalBody>{modalMessage}</ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
    </AdminLayout>
  )
}
